import React, { useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';

import { GlobalErrorView } from '@bingads-webui-react/app-global-error-view';

const DRAFT_ACCOUNT_STATUS = 30;

const ACCOUNT_TYPES = {
  expert: 'Expert',
  smart: 'Smart',
  unified: 'Unified Smart',
  mca: 'Multi Channel',
  subscriptionAds: 'Subscription Ads',
};

const ACCOUNT_MODES = {
  expert: '0',
  smart: '1',
  unified: '2',
};

function trimCampaignUrl(ServicesEndpoints, prefix = '', suffix = '') {
  let url = (ServicesEndpoints && ServicesEndpoints.CampaignUIAPI) || 'https://ui.ads.microsoft.com';
  url = url.toLowerCase().replace('ui.', prefix).replace('/campaign/', '').replace('/campaign', '');
  return `${url}${suffix}`;
}

function getSupportUrl({ ServicesEndpoints }) {
  return trimCampaignUrl(ServicesEndpoints, 'internal.');
}

function getSignupUrl({
  ServicesEndpoints,
  accountType,
  uid,
  aid,
  cid,
}) {
  let suffix = '';
  let prefix = '';
  switch (accountType) {
    case ACCOUNT_TYPES.subscriptionAds:
      prefix = '/subscription';
      break;
    case ACCOUNT_TYPES.expert:
    case ACCOUNT_TYPES.smart:
    case ACCOUNT_TYPES.unified:
      suffix = '';
      break;
    default:
      return null;
  }

  return trimCampaignUrl(ServicesEndpoints, '', `${prefix}/signup/${suffix}?uid=${uid}&cid=${cid}&aid=${aid}`);
}

function getIsInternal(CurrentUser, Permissions) {
  return CurrentUser.IsInternal || Permissions.IsInternalUser || false;
}

function getIsDraft(CurrentAccount) {
  return CurrentAccount.Status === DRAFT_ACCOUNT_STATUS;
}

export function getShouldBypass(search = window.location.search) {
  const query = (search && search.toLowerCase()) || '';
  return query.indexOf('bypassdraft') !== -1;
}

function getAccountType(CurrentAccount, DynamicPermissions) {
  if (DynamicPermissions.McaScope && CurrentAccount.AccountMode === null) {
    return ACCOUNT_TYPES.mca;
  } else if (DynamicPermissions.UnifiedProductSubscription && `${CurrentAccount.AccountMode}` === ACCOUNT_MODES.unified) {
    return ACCOUNT_TYPES.subscriptionAds;
  } else if (`${CurrentAccount.AccountMode}` === ACCOUNT_MODES.unified) {
    return ACCOUNT_TYPES.unified;
  } else if (`${CurrentAccount.AccountMode}` === ACCOUNT_MODES.smart) {
    return ACCOUNT_TYPES.smart;
  }
  return ACCOUNT_TYPES.expert;
}

export function DraftAccountPage(props) {
  const {
    serverContext,
    logTrace,
    logError,
    searchQuery,
    redirect,
  } = props;
  const CurrentAccount = (serverContext && serverContext.CurrentAccount) || {};
  const CurrentCustomer = (serverContext && serverContext.CurrentCustomer) || {};
  const CurrentUser = (serverContext && serverContext.CurrentUser) || {};
  const Permissions = (serverContext && serverContext.Permissions) || {};
  const DynamicPermissions = (serverContext && serverContext.DynamicPermissions) || {};
  const {
    Number: AccountNumber,
    Id: AccountId,
    AccountMode,
    Status: AccountStatus,
  } = CurrentAccount;
  const { Id: CustomerId } = CurrentCustomer;
  const { Id: UserId } = CurrentUser;

  const [bypass, setBypass] = useState(false);
  const shouldBypass = getShouldBypass(searchQuery);
  const isInternal = getIsInternal(CurrentUser, Permissions);
  const accountType = getAccountType(CurrentAccount, DynamicPermissions);
  const isDraft = getIsDraft(CurrentAccount);
  const supportUrl = getSupportUrl(serverContext);
  const signupUrl = getSignupUrl({
    ServicesEndpoints: serverContext.ServicesEndpoints,
    accountType,
    uid: UserId,
    aid: AccountId,
    cid: CustomerId,
  });

  useEffect(() => {
    if (isDraft) {
      const log = logTrace;
      log(`Draft account loaded: ${JSON.stringify({
        AccountId,
        AccountNumber,
        AccountMode,
        AccountStatus,
        CustomerId,
        UserId,
        IsInternalUser: isInternal,
        accountType,
        bypass,
      })}`);

      if (accountType === ACCOUNT_TYPES.unified && !isInternal) {
        redirect(signupUrl);
      }
    }
  }, [
    AccountId,
    AccountMode,
    AccountNumber,
    AccountStatus,
    CustomerId,
    UserId,
    accountType,
    bypass,
    isDraft,
    isInternal,
    logError,
    logTrace,
    signupUrl,
    redirect,
  ]);

  useEffect(() => {
    if (!props.children) {
      logError('DraftAccountPage: requires children prop');
    }
  }, [logError, props.children]);

  const description = useMemo(() => _.compact([
    'The customer must complete signup to access the main UI.',
    () => (<span>AccountId: <b>{AccountId}</b></span>),
    () => (<span>AccountNumber: <b>{AccountNumber}</b></span>),
    () => (<span>AccountMode: <b>{AccountMode}</b></span>),
    () => (<span>CustomerId: <b>{CustomerId}</b></span>),
    () => (
      <div>
        Support page: <a id="supportUrl" href={supportUrl}>{supportUrl}</a>
      </div>
    ),
    shouldBypass && (() =>
      (<button id="bypass" onClick={() => setBypass(true)} >Bypass page (use with caution)</button>)
    ),
    signupUrl && (() => (
      <div>
        View customer in signup page: <a id="signupUrl" href={signupUrl}>{signupUrl}</a>
      </div>
    )),
  ]), [AccountId, AccountMode, AccountNumber, CustomerId, shouldBypass, signupUrl, supportUrl]);

  const title = `Unsupported draft "${accountType}" account`;

  return (!bypass && isInternal && isDraft) ?
    (
      <GlobalErrorView
        className="draft-account-error-page"
        title={title}
        description={description}
      />
    ) :
    props.children;
}

DraftAccountPage.propTypes = {
  serverContext: PropTypes.shape({}).isRequired,
  logTrace: PropTypes.func.isRequired,
  logError: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  searchQuery: PropTypes.string,
  redirect: PropTypes.func,
};

DraftAccountPage.defaultProps = {
  searchQuery: undefined,
  redirect: (url) => { window.location.href = url; },
};
